import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'use-intl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaymentGroup, SigningSession, PaymentConfirmationStep } from '@/types/paymentConfirmation';
import { Invoice } from '@/types/payments';
import { groupInvoicesByBankAndCurrency, splitGroupsByBasketLimit } from '@/utils/paymentGroupingUtils';
import PaymentOverviewStep from './PaymentOverviewStep';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { Loader2 } from 'lucide-react';
import PaymentSuccessStep from './PaymentSuccessStep';
import { useKycErrorHandler } from '@/hooks/useKycErrorHandler';
import KycRequiredModal from '@/components/kyc/KycRequiredModal';
import PaymentAuthFlow from '@/components/payment/PaymentAuthFlow';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import type { BankConstraintInfo } from '@/types/bankConstraints';
import { accessNeededAccountsFrom, type RenewableAccount } from '@/lib/renewalRequest';
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface ConfirmPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedInvoices: Invoice[];
  actionInvoiceId: string | null;
  onConfirmPayment: () => void;
  refreshData?: () => Promise<void>;
  autoStartSigning?: boolean;
}

const ConfirmPaymentModal: React.FC<ConfirmPaymentModalProps> = ({
  open,
  onOpenChange,
  selectedInvoices,
  actionInvoiceId,
  onConfirmPayment,
  refreshData,
  autoStartSigning = false
}) => {
  const t = useTranslations('consultantPayments.confirm');
  // The latest translator, so the error handler below can word its fallback without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [currentStep, setCurrentStep] = useState<PaymentConfirmationStep>('overview');
  const [paymentGroups, setPaymentGroups] = useState<PaymentGroup[]>([]);
  const [constraintByBic, setConstraintByBic] = useState<Record<string, BankConstraintInfo | null>>({});
  const [currentSigningSession, setCurrentSigningSession] = useState<SigningSession | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [accessNeeded, setAccessNeeded] = useState<RenewableAccount[] | null>(null);
  const returnToOverviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // KYC error handling
  const { showKycModal, companyId, handleKycError, closeKycModal, handleKycCompleted } = useKycErrorHandler();

  // Get bank accounts for grouping by BIC
  const { accounts: bankAccounts, loadingAccounts } = useBankAccounts();

  // The selection the groups were last built for. A refreshed account list alone (e.g. after an
  // access renewal, FIN-885) must not rebuild them: that would discard signing progress.
  const groupsBuiltFor = useRef<{ invoices: Invoice[]; actionInvoiceId: string | null } | null>(null);

  // Initialize payment groups when modal opens or when selected invoices change
  useEffect(() => {
    const built = groupsBuiltFor.current;
    if (built && built.invoices === selectedInvoices && built.actionInvoiceId === actionInvoiceId) return;
    if (open && selectedInvoices.length > 0 && !loadingAccounts && bankAccounts.length > 0) {
      let invoicesToProcess = actionInvoiceId
        ? selectedInvoices.filter(inv => inv.id === actionInvoiceId)
        : selectedInvoices;
      // Filter out already paid invoices
      invoicesToProcess = invoicesToProcess.filter(inv => inv.status !== 'paid');
      if (invoicesToProcess.length > 0) {
        const groups = groupInvoicesByBankAndCurrency(invoicesToProcess, bankAccounts);
        groupsBuiltFor.current = { invoices: selectedInvoices, actionInvoiceId };
        setPaymentGroups(groups);
        setCurrentStep('overview');
        setCurrentSigningSession(null);
        setCurrentGroupId(null);
        // Pre-flight: fetch constraints, then auto-split groups that exceed maxBasketSize
        const uniqueBics = [...new Set(groups.map(g => g.bicFi ?? g.bankAccount).filter(Boolean))];
        const bankInfoService = ServiceRegistry.getBankInfoService();
        Promise.all(uniqueBics.map(bic => bankInfoService.getConstraint(bic))).then(results => {
          const map: Record<string, BankConstraintInfo | null> = {};
          uniqueBics.forEach((bic, i) => { map[bic] = results[i]; });
          setConstraintByBic(map);
          const split = splitGroupsByBasketLimit(groups, map);
          if (split.length !== groups.length) setPaymentGroups(split);
        });
      }
    }
  }, [open, selectedInvoices, actionInvoiceId, bankAccounts, loadingAccounts]); // Re-run when modal opens or selected invoices change

  // Auto-start signing when there's exactly one group and autoStartSigning is enabled
  useEffect(() => {
    if (autoStartSigning && open && paymentGroups.length === 1 && currentStep === 'overview') {
      const group = paymentGroups[0];
      if (group.status === 'pending') {
        handleStartSigning(group.id);
      }
    }
  }, [autoStartSigning, open, paymentGroups, currentStep]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      groupsBuiltFor.current = null;
      setCurrentStep('overview');
      setCurrentSigningSession(null);
      setCurrentGroupId(null);
      setPaymentGroups([]);
      setConstraintByBic({});
      setShowSuccessScreen(false);
      setPaymentError(null);
      setAccessNeeded(null);
    }
  }, [open]);

  const handleStartSigning = useCallback(async (groupId: string) => {
    const group = paymentGroups.find(g => g.id === groupId);
    if (!group) {
      return;
    }

    // Clear any previous errors
    setPaymentError(null);
    setAccessNeeded(null);

    // Set up the signing session immediately and let PaymentAuthFlow handle the auth
    setPaymentGroups(prev =>
      prev.map(g =>
        g.id === groupId ? { ...g, status: 'signing' as const } : g
      )
    );
    setCurrentGroupId(groupId);
    setCurrentStep('signing');
  }, [paymentGroups]);

  const handlePaymentCompleted = useCallback((result: any) => {
    if (!currentGroupId) return;

    const signingSession: SigningSession = {
      groupId: currentGroupId,
      invoices: paymentGroups.find(g => g.id === currentGroupId)?.invoices || [],
      qrCode: result.url || result.bankIdUrl || '',
      signingBasketId: result.signingBasketId,
      authorisationId: result.authorisationId,
      status: 'completed',
      progress: 100
    };

    setCurrentSigningSession(signingSession);
    handleSigningComplete(signingSession);  // Pass session directly to avoid state timing issues
  }, [currentGroupId, paymentGroups]);

  const handlePaymentError = useCallback((error: string | Error | unknown) => {
    console.log('handlePaymentError called with:', error);

    // Check if it's a KYC error first — the KYC dialog stacks on top of this modal (see
    // useKycErrorHandler's own comment), so this modal is deliberately left open/unchanged.
    if (handleKycError(error)) {
      // KYC error was handled, no need to show other errors
      console.log('KYC error detected and handled');
      return;
    }

    // A source account without bank access of its own: named, with a way to renew it.
    const accountsNeedingAccess = accessNeededAccountsFrom(error, bankAccounts);
    if (accountsNeedingAccess) {
      setAccessNeeded(accountsNeedingAccess);
      return;
    }

    // Extract error message from different formats
    let errorMessage = tRef.current('defaultError');

    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error instanceof Error) {
      errorMessage = userFacingErrorMessage(error, errorMessage);
    } else if (error && typeof error === 'object' && 'error' in error) {
      // Handle API response format: {error: "message"}
      errorMessage = String((error as { error: string }).error);
    }

    console.error('Payment error:', errorMessage);
    setPaymentError(errorMessage);
  }, [handleKycError, bankAccounts]);

  const handleBackToOverview = useCallback(() => {
    // A signed group's pending return must not later pull the user out of the next group's signing.
    if (returnToOverviewTimer.current) clearTimeout(returnToOverviewTimer.current);
    returnToOverviewTimer.current = null;
    // handleStartSigning flips the group to 'signing' the instant a user starts (not once
    // signing actually completes) — backing out of an interrupted attempt (e.g. a KYC gate,
    // issue #1020) must revert that, or PaymentOverviewStep permanently shows this group as
    // already "Signing…"/processed, with no way to ever retry it.
    setPaymentGroups(prev =>
      prev.map(g =>
        g.id === currentGroupId && g.status === 'signing' ? { ...g, status: 'pending' as const } : g
      )
    );
    setCurrentStep('overview');
    setCurrentSigningSession(null);
    setCurrentGroupId(null);
    setPaymentError(null);
    setAccessNeeded(null);
  }, [currentGroupId]);

  const handleSigningComplete = useCallback((session?: SigningSession) => {
    // Use provided session or fall back to state
    const activeSession = session || currentSigningSession;

    if (!activeSession || !activeSession.signingBasketId) {
      handleBackToOverview();
      return;
    }
    setPaymentGroups(prev => {
      const updated = prev.map(group =>
        group.id === activeSession.groupId
          ? { ...group, status: 'signed' as const }
          : group
      );
      // Check if there are more pending groups
      const remainingPendingGroups = updated.filter(group => group.status === 'pending');
      if (remainingPendingGroups.length === 0) {
        setShowSuccessScreen(true);
      }
      return updated;
    });
    returnToOverviewTimer.current = setTimeout(() => {
      handleBackToOverview();
    }, 1000);
  }, [currentSigningSession, handleBackToOverview]);

  const handleUpdateSigningSession = useCallback((session: SigningSession) => {
    setCurrentSigningSession(session);
  }, []);

  const currentGroup = useMemo(() => {
    return currentGroupId ? paymentGroups.find(g => g.id === currentGroupId) : null;
  }, [currentGroupId, paymentGroups]);

  let titleKey: 'completed' | 'overview' | 'signing' = 'signing';
  if (showSuccessScreen) titleKey = 'completed';
  else if (currentStep === 'overview') titleKey = 'overview';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" hideCloseButton>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {t(`titles.${titleKey}`)}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        {showSuccessScreen ? (
          <PaymentSuccessStep onComplete={async () => {
            setShowSuccessScreen(false);
            onConfirmPayment();
            // Refresh payables data to get updated statuses from server
            if (refreshData) {
              await refreshData();
            }
            onOpenChange(false);
          }} />
        ) : (
          <div className="overflow-y-auto">
            {currentStep === 'overview' && paymentGroups.length > 0 && (
              <PaymentOverviewStep
                paymentGroups={paymentGroups}
                onStartSigning={handleStartSigning}
              />
            )}
            {currentStep === 'signing' && currentGroup && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={handleBackToOverview}
                    className="h-8 px-3"
                  >
                    {t('back')}
                  </Button>
                </div>
                <PaymentAuthFlow
                  payableIds={currentGroup.invoices.map(inv => inv.payableId || inv.id)}
                  onPaymentCompleted={handlePaymentCompleted}
                  onError={handlePaymentError}
                  error={paymentError}
                  accessNeeded={accessNeeded}
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* KYC Required Modal */}
      {showKycModal && companyId && (
        <KycRequiredModal
          open={showKycModal}
          onOpenChange={closeKycModal}
          companyId={companyId}
        />
      )}
    </Dialog>
  );
};

export default ConfirmPaymentModal;
